import { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { AVATARS } from '@/data/mockData';
import { getTimeRemainingShort } from '@/components/shared/CountdownTimer';
import { LOGO_URL } from '@/data/mockData';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Plus, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ClientSidebar({ onSelect, onShowUserPanel, onShowCredits, onShowHistory }) {
  const { projects, activeClientProjectId, setActiveClientProjectId, createProject, isLoggedIn } = useApp();
  const { user, profile } = useAuth();
  const [, tick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => tick(n => n + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const handleNewProject = () => {
    const id = createProject('New Video Project');
    setActiveClientProjectId(id);
    onSelect?.();
  };

  const handleSelectProject = (id) => {
    setActiveClientProjectId(id);
    onSelect?.();
  };

  // --- Display info: real profile when logged in, mock fallback when anonymous ---
  const displayName = isLoggedIn
    ? (profile?.full_name?.trim() || user?.email?.split('@')[0] || 'Set up profile')
    : 'Carlos M.';
  const displayAvatar = isLoggedIn
    ? (profile?.avatar_url || null)
    : AVATARS[0];
  const credits = isLoggedIn ? (profile?.credits ?? 0) : 12;
  const profileIncomplete = isLoggedIn && !profile?.full_name?.trim();

  const getSecondaryText = (project) => {
    if (project.status === 'new') return null;
    if (project.status === 'ready') return 'ready';
    if (project.status === 'delivered') return 'delivered';
    if (project.status === 'revision_requested') return 'revision';
    const time = getTimeRemainingShort(project.deliveryDeadline);
    return time ? `timer:${time}` : '';
  };

  const renderSecondary = (project) => {
    const val = getSecondaryText(project);
    if (!val) return null;
    if (val === 'ready') return (
      <span className="flex items-center gap-1.5 mt-0.5">
        <span className="text-[11px] text-emerald-600 font-medium">Ready</span>
        {project.hasNewUpdate && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />}
      </span>
    );
    if (val === 'delivered') return <span className="text-[11px] text-emerald-600 mt-0.5 block">Delivered</span>;
    if (val === 'revision') return <span className="text-[11px] text-amber-600 mt-0.5 block font-medium">In Revision</span>;
    if (val.startsWith('timer:')) return <span className="text-[11px] mt-0.5 block font-mono font-semibold animate-breathe-text" style={{ color: '#B8860B' }}>{val.slice(6)}</span>;
    return <span className="text-[11px] text-gray-400 mt-0.5 block">{val}</span>;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-1 flex items-center">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full overflow-hidden shrink-0">
            <img src={LOGO_URL} alt="Ruido.ai" className="w-full h-full object-cover scale-[1.15]" />
          </div>
          <span className="text-sm font-medium text-gray-900 font-['Outfit']">Ruido<span className="text-gray-400">.ai</span></span>
        </Link>
      </div>
      <div className="px-5 pb-3 pt-2">
        <button
          onClick={() => onShowCredits?.()}
          data-testid="credits-pill"
          className="glass-pill px-3 py-1 rounded-full text-[11px] font-medium text-gray-500 hover:text-gray-900 transition-colors"
        >
          <span className="text-emerald-500 font-semibold">{credits}</span> credits
        </button>
      </div>

      <div className="px-3 pb-3">
        <Button
          data-testid="new-project-btn"
          onClick={handleNewProject}
          className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-9 text-sm font-medium transition-all active:scale-[0.98]"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Project
        </Button>
      </div>

      <Separator className="bg-gray-100" />

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-0.5">
          {projects.length === 0 ? (
            <div className="text-center py-8 px-3">
              <p className="text-xs text-gray-400">No projects yet.</p>
              <p className="text-[11px] text-gray-300 mt-1">Tap New Project to get started.</p>
            </div>
          ) : projects.map((project) => (
            <button
              key={project.id}
              data-testid={`project-item-${project.id}`}
              onClick={() => handleSelectProject(project.id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-all ${
                activeClientProjectId === project.id
                  ? 'bg-gray-100'
                  : 'hover:bg-gray-50'
              }`}
            >
              <span className="text-sm text-gray-900 truncate block">{project.name}</span>
              {renderSecondary(project)}
            </button>
          ))}
        </div>
      </ScrollArea>

      <Separator className="bg-gray-100" />

      <div className="px-3 py-2 space-y-1">
        <button
          data-testid="history-btn"
          onClick={() => onShowHistory?.()}
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors"
        >
          <Clock className="w-4 h-4" />
          Historial
        </button>
      </div>

      <Separator className="bg-gray-100" />

      {/* User profile */}
      <div className="p-3">
        <button
          data-testid="user-profile-btn"
          onClick={() => onShowUserPanel?.()}
          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
        >
          {displayAvatar ? (
            <img
              src={displayAvatar}
              alt="Profile"
              className="w-8 h-8 rounded-full object-cover bg-gray-100 shrink-0"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center shrink-0 text-xs font-semibold text-gray-500">
              {(displayName || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <span className="text-sm text-gray-900 block truncate">{displayName}</span>
            <span className="text-[11px] text-gray-400">
              {profileIncomplete ? 'Complete your profile' : 'Active'}
            </span>
          </div>
          {profileIncomplete && (
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
          )}
        </button>
        <Link
          to="/gestor"
          data-testid="hidden-manager-link"
          className="block text-center pt-2 text-[9px] text-gray-300/40 hover:text-gray-400/60 transition-colors select-none"
        >
          internal
        </Link>
      </div>
    </div>
  );
}
